import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core'
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators
} from '@angular/forms'
import { Actions, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'
import moment from 'moment-mini'
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal'
import { Observable, Subscription, combineLatest, of } from 'rxjs'
import {
  catchError,
  filter,
  map,
  startWith,
  take,
  timeout,
  withLatestFrom
} from 'rxjs/operators'
import { AppState } from 'src/app/core/core.state'
import { NotificationService } from 'src/app/core/notification.service'

import { Router } from '@angular/router'
import { CondOperator, RequestQueryBuilder } from '@nestjsx/crud-request'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { Dictionary } from 'highcharts'
import { settingSelectors } from 'src/app/core/settings/settings.selectors'
import { Booking } from 'src/app/features/booking/booking'
import { bookingActions } from 'src/app/features/booking/booking.actions'
import { Location } from 'src/app/features/location/location'
import { locationSelectors } from 'src/app/features/location/location.selectors'
import { Member } from 'src/app/features/member/member'
import { memberSelectors } from 'src/app/features/member/member.selectors'
import { Organization } from 'src/app/features/organization/organization'
import { organizationSelectors } from 'src/app/features/organization/organization.selectors'
import { Product } from 'src/app/features/product/product'
import { productActions } from 'src/app/features/product/product.actions'
import { productSelectors } from 'src/app/features/product/product.selectors'
import { persistentUserRoleSelectors } from 'src/app/features/role/user-role/persistent-user-role/persistent-user-role.selector'
import { SerieProposal, SerieProposalStatus } from '../serie-proposal'
import { SerieProposalItem } from '../serie-proposal-item/serie-proposal-item'
import { serieProposalItemActions } from '../serie-proposal-item/serie-proposal-item.actions'
import { serieProposalItemSelectors } from '../serie-proposal-item/serie-proposal-item.selectors'
import { serieProposalActions } from '../serie-proposal.actions'
import { serieProposalSelectors } from '../serie-proposal.selectors'
import { ConfirmationModalComponent } from '../series-tab/confirmation-modal/confirmation-modal.component'
import { configurationOptionSelectors } from 'src/app/features/configuration-option/configuration-option.selectors'
import { locationActions } from 'src/app/features/location/location.actions'

@UntilDestroy()
@Component({
  templateUrl: './show.component.html',
  styleUrls: ['./show.component.scss']
})
export class SerieProposalEditModalComponent implements OnInit {
  @Input() serieProposal: SerieProposal
  @Input() repeatableBooking: any
  @Output() onCancel = new EventEmitter<boolean>()
  public form: UntypedFormGroup
  public productEntities$: Observable<Dictionary<Product>>
  public memberEntities$: Observable<Dictionary<Member>>
  public locationEntities$: Observable<Dictionary<Location>>
  public organizationEntities$: Observable<Dictionary<Organization>>
  public selectedLanguage$: Observable<string>
  public locations$: Observable<Location[]>
  public isLoading$: Observable<boolean>
  public serieProposalStatus = SerieProposalStatus
  public bsModalRefConfirmationModal: BsModalRef
  public daysOfWeek: number[] = [1, 2, 3, 4, 5, 6, 7]
  public resources$: Observable<Product[]>
  private serieProposalProductGroupIds: number[] = []
  private subscriptions: Subscription[] = []
  private locationIds: number[] = []

  constructor(
    private bsModalRef: BsModalRef,
    private store: Store<AppState>,
    private fb: UntypedFormBuilder,
    private actions$: Actions,
    private notificationService: NotificationService,
    private router: Router,
    private modalService: BsModalService
  ) {}

  ngOnInit() {
    this.form = this.fb.group({
      day: [
        this.repeatableBooking ? this.repeatableBooking.dayOfWeek : null,
        [Validators.required]
      ],
      startTime: [
        this.repeatableBooking?.startTime
          ? moment().set({
              hour: moment(this.repeatableBooking.startTime, 'HH:mm').hour(),
              minute: moment(this.repeatableBooking.startTime, 'HH:mm').minute()
            })
          : null,
        [Validators.required]
      ],
      endTime: [
        this.repeatableBooking?.endTime
          ? moment().set({
              hour: moment(this.repeatableBooking.endTime, 'HH:mm').hour(),
              minute: moment(this.repeatableBooking.endTime, 'HH:mm').minute()
            })
          : null,
        [Validators.required]
      ],
      locationId: [null, [Validators.required]],
      productId: [null, [Validators.required]],
      dontSendMail: [false, [Validators.required]]
    })

    this.isLoading$ = this.store.select(
      serieProposalItemSelectors.selectIsLoading
    )

    this.selectedLanguage$ = this.store.select(
      settingSelectors.selectById,
      'language'
    )

    this.productEntities$ = this.store.select(productSelectors.selectEntities)
    this.memberEntities$ = this.store.select(memberSelectors.selectEntities)
    this.locationEntities$ = this.store.select(locationSelectors.selectEntities)
    this.organizationEntities$ = this.store.select(
      organizationSelectors.selectEntities
    )

    this.productEntities$.pipe(take(1)).subscribe((productEntities) => {
      const locationId =
        productEntities[this.serieProposal.productId]?.locationId

      this.form.controls.locationId.setValue(locationId)
    })

    this.store
      .select(configurationOptionSelectors.selectByKey, 'serie_proposal_config')
      .pipe(
        filter((configurationOption) => Boolean(configurationOption)),
        take(1),
        map((configurationOption) => JSON.parse(configurationOption?.value))
      )
      .subscribe((serieProposalConfigOptionValue) => {
        this.locationIds = serieProposalConfigOptionValue?.locationIds ?? []
        this.serieProposalProductGroupIds =
          serieProposalConfigOptionValue?.productGroupIds ?? []

        this.fetchLocations()
        this.fetchProducts()

        return
      })

    this.resources$ = combineLatest([
      this.store.select(productSelectors.selectEntities),
      this.form.controls.locationId.valueChanges.pipe(startWith(null))
    ]).pipe(
      untilDestroyed(this),
      map(([productEntities, locationIdValueChange]) =>
        Object.values(productEntities).filter(
          (product) =>
            product.bookable &&
            product.locationId == this.form.value.locationId &&
            product.productGroups?.some((productGroupId) =>
              this.serieProposalProductGroupIds.includes(productGroupId)
            )
        )
      )
    )

    this.locations$ = this.store.select(locationSelectors.selectAll).pipe(
      untilDestroyed(this),
      map((allLocations) => {
        if (!this.locationIds) {
          return []
        }
        return allLocations.filter((location) =>
          this.locationIds.includes(location.id)
        )
      })
    )

    this.form.controls.locationId.valueChanges
      .pipe(untilDestroyed(this), withLatestFrom(this.productEntities$))
      .subscribe(([locationId, productEntities]) => {
        if (
          locationId !=
          productEntities[this.serieProposal.productId]?.locationId
        ) {
          // fetch products of location
          this.fetchProducts(locationId)

          this.form.controls.productId.setValue(null)
          this.form.controls.productId.markAsDirty()
        } else {
          this.form.controls.productId.setValue(this.serieProposal.productId)
        }
      })
  }

  public onSearchProducts(term: string) {
    this.store.dispatch(
      productActions.fetchMany({
        params: RequestQueryBuilder.create({
          search: {
            description: { [CondOperator.CONTAINS_LOW]: term }
          }
        })
      })
    )
  }

  public isDaySelected(day: number): boolean {
    return this.form.value.day == day
  }

  public isDayDisabled(day: number): boolean {
    return Boolean(
      this.serieProposal.details.repeatableBookings.find(
        (booking) =>
          booking.dayOfWeek === day && booking !== this.repeatableBooking
      )
    )
  }

  public onClickSendCounterProposal() {
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach((key) => {
        this.form.controls[key].markAsDirty()
      })
      console.log('%cForm is invalid', 'color: red; font-weight: bold;')
      return
    }

    const startTime = moment(this.form.value.startTime)
      .set({ second: 0 })
      .format('HH:mm:ss')
    const endTime = moment(this.form.value.endTime)
      .set({ second: 0 })
      .format('HH:mm:ss')

    const start = moment().set({
      hour: moment(startTime, 'HH:mm').hour(),
      minute: moment(startTime, 'HH:mm').minute()
    })

    const end = moment().set({
      hour: moment(endTime, 'HH:mm').hour(),
      minute: moment(endTime, 'HH:mm').minute()
    })

    if (end.isSameOrBefore(start)) {
      this.notificationService.emit(
        'employeeModule.endTimeEarlierThanStartTime',
        'warning'
      )
      return
    }

    const updatedRepeatableBookings =
      this.serieProposal.details.repeatableBookings.map((booking) => {
        if (booking.dayOfWeek === this.repeatableBooking.dayOfWeek) {
          return {
            ...booking,
            dayOfWeek: this.form.value.day,
            startTime,
            endTime
          }
        }
        return booking
      })

    // mark
    console.log(
      'onClickSendCounterProposal selected product',
      this.form.value.productId
    )
    if (this.serieProposal.serieProposalItems?.length > 0) {
      this.store.dispatch(
        serieProposalItemActions.removeMany({
          ids: this.serieProposal.serieProposalItems.map((it) => it)
        })
      )
    }

    this.store.dispatch(
      serieProposalActions.updateOneById({
        id: this.serieProposal.id,
        params: {
          details: {
            ...this.serieProposal.details,
            repeatableBookings: updatedRepeatableBookings
          },
          status: SerieProposalStatus.COUNTER_PROPOSAL,
          productId: this.form.value.productId,
          dontSendMail: this.form.value.dontSendMail
        }
      })
    )
    this.actions$
      .pipe(
        ofType(serieProposalActions.upsertMany),
        take(1),
        timeout(5000),
        catchError((error) => of(null))
      )
      .subscribe((action) => {
        if (this.serieProposal) {
          this.store.dispatch(
            serieProposalActions.fetchOneById({
              id: this.serieProposal.id,
              params: RequestQueryBuilder.create({
                join: [
                  { field: 'customer' },
                  { field: 'organization' },
                  { field: 'organization.tags' },
                  { field: 'serieProposalItems' },
                  { field: 'serieProposalItems.saleItem' }
                ]
              })
            })
          )
          this.store.dispatch(
            serieProposalItemActions.fetchMany({
              params: RequestQueryBuilder.create({
                search: {
                  serieProposalId: {
                    [CondOperator.IN]: [this.serieProposal.id]
                  }
                },
                join: [
                  { field: 'serieProposal' },
                  { field: 'linkedProduct' },
                  { field: 'linkedProduct.translations' }
                ]
              })
            })
          )
        }
        if (action) {
          this.notificationService.emit('alerts.success', 'success')
          this.onCancel.emit(true)
          this.cancel()
        }
      })
  }

  public getSerieProposalItems$(
    proposal: SerieProposal
  ): Observable<SerieProposalItem[]> {
    return this.store.select(serieProposalItemSelectors.selectByFilter, {
      field: 'serieProposalId',
      value: proposal.id
    })
  }

  public onClickDeclineProposalItem() {
    this.store.dispatch(
      serieProposalActions.updateOneById({
        id: this.serieProposal.id,
        params: {
          status: SerieProposalStatus.DECLINED
        }
      })
    )

    this.handleUpsertManyAction()
  }

  public showConfirmationModal() {
    const mainModal = document.getElementById('mainModal')

    if (mainModal) {
      mainModal.classList.add('fadeModal')
    }

    this.bsModalRefConfirmationModal = this.modalService.show(
      ConfirmationModalComponent,
      {
        initialState: {
          title: 'serieProposalModule.hasRecentConflictingBooking',
          buttons: [
            {
              text: 'general.close',
              class: 'btn btn-soft-primary',
              accept: false
            },
            {
              text: 'general.accept',
              class: 'btn btn-primary',
              accept: true
            }
          ]
        },
        class: 'modal-md modal-dialog-scrollable'
      }
    )

    this.bsModalRefConfirmationModal.onHide
      .pipe(take(1))
      .subscribe((modal) => mainModal.classList.remove('fadeModal'))

    this.subscriptions.push(
      this.bsModalRefConfirmationModal.content.onAccept
        .pipe(take(1))
        .subscribe((acceptItemsWithConflictingBookings) => {
          mainModal.classList.remove('fadeModal')

          if (acceptItemsWithConflictingBookings) {
            this.store.dispatch(
              serieProposalActions.updateOneById({
                id: this.serieProposal.id,
                params: {
                  id: this.serieProposal.id,
                  status: SerieProposalStatus.ACCEPTED
                }
              })
            )

            return this.handleUpsertManyAction()
          }
        })
    )
  }

  public onClickRow(booking: Booking) {
    const url = this.router.serializeUrl(
      this.router.createUrlTree([`/bookings/${booking.id}`])
    )
    window.open(url, '_blank')
  }

  public userHasAccessToSerieProposal$(): Observable<boolean> {
    return this.store
      .select(persistentUserRoleSelectors.selectSelectedUserRole)
      .pipe(
        withLatestFrom(
          this.store.select(
            serieProposalSelectors.selectById,
            this.serieProposal.id
          )
        ),
        take(1),
        map(([pUr, serieProposal]) => {
          const siteId = pUr && pUr.siteId ? pUr.siteId : 0
          return siteId === serieProposal?.siteId
        })
      )
  }

  public cancel() {
    this.bsModalRef.hide()
  }

  private fetchLocations() {
    if (!this.locationIds?.length) {
      console.log(
        'No locationIds described in serie_proposal_config web option'
      )
      return
    }

    this.store.dispatch(
      locationActions.fetchMany({
        params: RequestQueryBuilder.create({
          search: { id: { [CondOperator.IN]: this.locationIds } }
        })
      })
    )
  }

  private fetchProducts(locationId?: number) {
    if (!this.serieProposalProductGroupIds?.length) {
      console.log(
        'No productGroupIds described in serie_proposal_config web option'
      )
      return
    }

    this.store
      .select(productSelectors.selectEntities)
      .pipe(take(1))
      .subscribe((productEntities) => {
        const locationIdToFetch = locationId
          ? locationId
          : productEntities[this.serieProposal.productId]?.locationId

        this.store.dispatch(
          productActions.fetchMany({
            params: RequestQueryBuilder.create({
              search: {
                locationId: locationIdToFetch,
                bookable: true,
                isActive: true,
                'productGroups.id': {
                  [CondOperator.IN]: [...this.serieProposalProductGroupIds]
                }
              },
              join: [{ field: 'productGroups' }]
            })
          })
        )
      })
  }

  private handleUpsertManyAction() {
    this.actions$
      .pipe(
        ofType(serieProposalItemActions.upsertMany),
        take(1),
        timeout(5000),
        catchError((error) => of(null))
      )
      .subscribe((action) => {
        if (action) {
          this.notificationService.emit('alerts.success', 'success')
          this.onCancel.emit(true)
          this.cancel()
        }
      })
  }

  private fetchIntersectingBookings() {
    if (!this.serieProposal) {
      return
    }

    this.productEntities$.pipe(take(1)).subscribe((productEntities) => {
      let dependantProductIds: number[] = Array.from(
        new Set(
          productEntities[this.serieProposal.productId]?.dependantProducts ?? []
        )
      )

      const productIds = Array.from(
        new Set([...dependantProductIds, this.serieProposal.productId])
      )

      this.store.dispatch(
        bookingActions.fetchMany({
          params: RequestQueryBuilder.create({
            search: {
              startDate: {
                [CondOperator.LOWER_THAN]: moment(
                  this.serieProposal.endDate
                ).format('YYYY-MM-DD HH:mm')
              },
              endDate: {
                [CondOperator.GREATER_THAN]: moment(
                  this.serieProposal.startDate
                ).format('YYYY-MM-DD HH:mm')
              },
              $or: [
                productIds?.length
                  ? {
                      productId: {
                        [CondOperator.IN]: productIds
                      }
                    }
                  : null,
                productIds?.length
                  ? {
                      linkedProductId: {
                        [CondOperator.IN]: productIds
                      }
                    }
                  : null
              ].filter((c) => Boolean(c))
            }
          })
        })
      )
    })
  }
}
